import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { getBuildVersion } from "@/helpers/buildVersion";
// LSP-17773: the claim-token/credential scrubber is shared with src/api/index.ts (HTTP error
// logging) so both use one redaction implementation — see the helper for the threat model.
import { scrubClaimToken } from "@/helpers/scrubClaimToken";
// LSP-19866: browser-extension errors (Microsoft Editor, MetaMask, ...) thrown inside our page are
// reported by both SDKs as Contact Admin errors — drop them before they leave the browser.
import { isBrowserExtensionNoise } from "@/helpers/isBrowserExtensionNoise";
import { registerDashboardLaunchContext } from "@/helpers/launchContextTelemetry";

const initializeDatadog = {
	install() {
		const env = (window as any).env?.ENVIRONMENT || (window as any).ENVIRONMENT;
		// No reason to collect telemetry from local dev boxes or CI runs — both flood
		// production Datadog with non-user traffic (e.g. Playwright fetches to the
		// docker-compose sibling containers on localhost).
		if (env === "development" || env === "ci") {
			return;
		}

		const version = getBuildVersion();

		datadogLogs.init({
			clientToken: (window as any).env?.VITE_DATA_DOG || (window as any).DATA_DOG,
			service: "contact-admin",
			env: env,
			version: version,
			site: "datadoghq.com",
			forwardErrorsToLogs: true,
			// LSP-15979: forward warn + info (not just error) so the upload pipeline's diagnostic
			// logs reach Datadog. The Datadog SDK treats "log" and "info" as DISTINCT levels and only
			// forwards the names listed here — so upload diagnostics use console.info DELIBERATELY.
			// Keep new upload-pipeline logs on console.info (not console.log) or they won't forward.
			// "log" is intentionally omitted to bound browser-log ingestion volume/cost at
			// sessionSampleRate:100 (console.log stays local-only).
			forwardConsoleLogs: ["error", "warn", "info"],
			sessionSampleRate: 100,
			// Every log carries the current view URL/referrer — redact the claim token there too.
			// Log MESSAGES can also embed a token-bearing URL (e.g. api/index.ts's "Http Error:
			// … Url=…" console.error, or an Error whose message quotes the request URL).
			beforeSend: (event: any) => {
				// LSP-19866: decide drop FIRST, then scrub. Only error-STATUS logs are candidates.
				// AI review: gating on event.error.message too would make every warn/info diagnostic
				// that carries an attached Error a drop candidate (logWarn in helpers/logError.ts does
				// exactly that, and forwardConsoleLogs includes "warn") — info/warn never drop.
				if (event?.status === "error" && isBrowserExtensionNoise(event?.error?.message ?? event?.message, event?.error?.stack)) {
					return false;
				}
				if (event?.view?.url) event.view.url = scrubClaimToken(event.view.url);
				if (event?.view?.referrer) event.view.referrer = scrubClaimToken(event.view.referrer);
				if (event?.message) event.message = scrubClaimToken(event.message);
				if (event?.error?.message) event.error.message = scrubClaimToken(event.error.message);
				// Forwarded errors carry a stack whose frames (and quoted request URLs) can embed the
				// token; network-error logs can carry the failing resource URL.
				if (event?.error?.stack) event.error.stack = scrubClaimToken(event.error.stack);
				// Review: on the LOGS schema the failing request's URL lives at http.url (the
				// error.resource.url shape is RUM-only — kept above for forwarded shapes that
				// carry it anyway). A network error against /share-claims/<token> logs here.
				if (event?.http?.url) event.http.url = scrubClaimToken(event.http.url);
				if (event?.error?.resource?.url) event.error.resource.url = scrubClaimToken(event.error.resource.url);
				return true;
			}
		});

		// Propagate RUM → APM traces to contact-api by injecting tracing headers into
		// requests that match our API origin. contact-api's dd-trace picks these up.
		// env.json only exposes VITE_-prefixed keys (see create-environment-json.js);
		// the rest of contact-admin reads the API base from window.env.VITE_API_ROOT.
		const apiBase = (window as any).env?.VITE_API_ROOT as string | undefined;
		datadogRum.init({
			applicationId: "9ea904f1-8f50-464b-a13c-bd00dfee54ce",
			clientToken: "pub5daa5b7da74426bd1ad77ac7b239ffd3",
			site: "datadoghq.com",
			service: "contact-admin",
			env: env,
			version: version,
			sessionSampleRate: 100,
			sessionReplaySampleRate: 20,
			// Review (critical): Session Replay is a SEPARATE capture pipeline — beforeSend
			// never sees the recorded DOM/URL, so a replayed /share/:token session would ship
			// the still-claimable token in cleartext. Recording is started manually below,
			// and ONLY off the claim route (a claim session is a terminal, single-purpose
			// page — losing its replay costs nothing).
			startSessionReplayRecordingManually: true,
			trackBfcacheViews: true,
			defaultPrivacyLevel: "mask-user-input",
			allowedTracingUrls: apiBase ? [apiBase] : undefined,
			// Strip the credential-equivalent claim token from every event's URLs before send.
			beforeSend: (event: any) => {
				// LSP-19866: decide drop FIRST, then scrub. Only RUM error events are candidates —
				// view/action/resource events are never dropped, whatever text they carry.
				if (event?.type === "error" && isBrowserExtensionNoise(event?.error?.message ?? event?.message, event?.error?.stack)) {
					return false;
				}
				if (event?.view?.url) event.view.url = scrubClaimToken(event.view.url);
				if (event?.view?.referrer) event.view.referrer = scrubClaimToken(event.view.referrer);
				if (event?.resource?.url) event.resource.url = scrubClaimToken(event.resource.url);
				// RUM error events carry the thrown message, which can quote a token-bearing URL.
				if (event?.error?.message) event.error.message = scrubClaimToken(event.error.message);
				// A RUM error's provoking resource (failed fetch/XHR) and its stack frames can also
				// carry the token-bearing URL — scrub both, same as resource.url/error.message above.
				if (event?.error?.resource?.url) event.error.resource.url = scrubClaimToken(event.error.resource.url);
				if (event?.error?.stack) event.error.stack = scrubClaimToken(event.error.stack);
				return true;
			}
		});

		// Manual replay start (see startSessionReplayRecordingManually above): recording can only
		// begin AFTER datadogRum.init(...) has run, and never on the claim route. The claim page
		// is a hard entry point (emailed link), and in-app navigation cannot reach /share/:token,
		// so an initial-path check suffices.
		if (!window.location.pathname.startsWith("/share/")) {
			datadogRum.startSessionReplayRecording?.();
		}

		// LSP-21256: after both SDKs are initialized, so the startup event and global context reach both.
		registerDashboardLaunchContext();
	}
};

export default initializeDatadog;
